<template>
  <h1 class="component-heading">Tree</h1>
  <p class="component-description">
    A tree is a nonlinear hierarchical component that consists of nodes
    connected by edges.
  </p>
  <h2 class="component-side-heading">Basic usage</h2>
  <p class="component-description">
    Set <b>:tree-direction</b> to view the tree in specified direction<br />
    Define tree input data in <b>:node</b>
  </p>
  <div class="component-example">
    <!-- <button class="direction1" @click="myFunction()">Change view</button> -->
    <div class="example-body custom">
      <span class="custom"
        ><hlx-button class="primary large" @click="myFunction()"
          >Change view</hlx-button
        ></span
      >
      <div class="tree-container">
        <!-- <ul class="tree" :class="tree_direction" > -->
        <hlx-tree :node="treedata" :tree-direction="tree_direction" />
        <!-- </ul> -->
      </div>
    </div>
    <div class="example-footer">
      <span id="basic-tree-icon" class="icon" @click="showCode('basic-tree')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>
  <div id="basic-tree" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="basic_tree"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Drag usage</h2>
  <p class="component-description">
    <b>isdraggable</b> to <b>true</b> make the tree draggable<br />
  </p>
  <div class="component-example">
    <!-- <button class="direction1" @click="myFunction()">Change view</button> -->
    <div class="example-body custom">
      <!-- <span class="custom"
        ><hlx-button class="primary large" @click="myFunction()"
          >Change view</hlx-button
        ></span
      > -->
      <div class="tree-container">
        <!-- <ul class="tree" :class="tree_direction" > -->
        <hlx-tree
          :node="treedata"
          :tree-direction="tree_direction"
          :is-draggable="true"
        />
        <!-- </ul> -->
      </div>
    </div>
    <div class="example-footer">
      <span id="drag-tree-icon" class="icon" @click="showCode('drag-tree')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>
  <div id="drag-tree" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="drag_tree"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Zoom usage</h2>
  <p class="component-description">
    Set <b>:zoomable</b> zoom the tree<br />
    and specify the <b>:zoom</b> percentage of (10 - 200) to zoom <br />
  </p>
  <section class="switcher">
    <hlx-button class="primary large" @click="zoomin()">zoom in</hlx-button>
    <hlx-button class="primary large" @click="zoomout()">zoom out</hlx-button>
  </section>
  <div class="component-example">
    <div class="example-body custom">
      <span class="custom">
        <!-- <hlx-button class="primary large" @click="myFunction()"
          >Change view</hlx-button
        > -->
      </span>
      <div class="tree-container">
        <hlx-tree
          :node="treedata"
          :tree-direction="tree_direction"
          zoomable
          :zoom="zoom"
        />
      </div>
    </div>
    <div class="example-footer">
      <span
        id="zoomable-tree-icon"
        class="icon"
        @click="showCode('zoomable-tree')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>
  <div id="zoomable-tree" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="zoomable_tree"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Collapse usage</h2>
  <p class="component-description">
    pass <b>:collapse-all</b> and <b>:expandAll</b> as boolean to expand and
    collapse all the tree nodes
  </p>
  <section class="switcher">
    <hlx-button class="primary large" @click="expand()">{{
      isCollapsed ? "Expand all" : "Collapse all"
    }}</hlx-button>
  </section>
  <div class="component-example">
    <div class="example-body custom">
      <!-- <span class="custom">
        <hlx-button class="primary large" @click="myFunction()"
          >Change view</hlx-button
        >
      </span> -->
      <div class="tree-container">
        <hlx-tree
          :node="treedata"
          :tree-direction="tree_direction"
          :collapse-all="expandAll"
          :expand-all="collapseAll"
        />
      </div>
    </div>
    <div class="example-footer">
      <span id="expandable-icon" class="icon" @click="showCode('expandable')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>
  <div id="expandable" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="expandable_tree"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>

  <h2 class="component-side-heading">Product Tree</h2>
  <div class="component-example">
    <div
      class="example-body3"
      style="background-color: white; border-radius: 5px"
    >
      <hlx-tree-v2
        :nodes="treedataProduct"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="type"
        :config="configValue"
        :read-only="false"
        :is-draggable="true"
        :tree-data-props="treedataval"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @updated-node="updatednode"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
    </div>
    <div class="example-footer">
      <span
        id="treedata-product-icon"
        class="icon"
        @click="showCode('treedata-product')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>

  <div id="treedata-product" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="treedata_product"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>

  <h2 class="component-side-heading">Service Tree</h2>
  <p class="component-description">
    Service <b>type</b> may have values such as <b>Customer Facing Service</b>,
    <b>Resource Facing Service</b>.
  </p>
  <h2 class="component-side-heading">Customer Facing Service Tree</h2>
  <div class="component-example">
    <div
      class="example-body3"
      style="background-color: white; border-radius: 5px"
    >
      <hlx-tree-v2
        :nodes="treedataServiceCfs"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'service'"
        :read-only="false"
        :config="configValue"
        :is-draggable="true"
        :tree-data-props="treedataval"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
    </div>
    <div class="example-footer">
      <span
        id="treedata-service-cfs-icon"
        class="icon"
        @click="showCode('treedata-service-cfs')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>

  <div id="treedata-service-cfs" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="treedata_service_cfs"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Resource Facing Service Tree</h2>
  <div class="component-example">
    <div
      class="example-body3"
      style="background-color: white; border-radius: 5px"
    >
      <hlx-tree-v2
        :nodes="treedataHierarchy"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'orderTree'"
        :read-only="service"
        :config="configValue"
        :is-draggable="true"
        :tree-data-props="treedataval"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
    </div>
    <div class="example-footer">
      <span
        id="treedata-service-rfs-icon"
        class="icon"
        @click="showCode('treedata-service-rfs')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>

  <div id="treedata-service-rfs" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="treedata_service_rfs"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Resource Facing Service Tree</h2>
  <div class="component-example">
    <div
      class="example-body3"
      style="background-color: white; border-radius: 5px"
    >
      <hlx-tree-v2
        :nodes="treedataHierarchy"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'equipmentOrderTree'"
        :read-only="service"
        :config="configValue"
        :is-draggable="true"
        :tree-data-props="treedataval"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
    </div>
    <div class="example-footer">
      <span
        id="treedata-service-rfs-icon"
        class="icon"
        @click="showCode('treedata-service-rfs')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>

  <div id="treedata-service-rfs" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="treedata_service_rfs"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>
  <h2 class="component-side-heading">Attributes</h2>
  <hlx-table
    :column-count="6"
    :border="['table', 'header', 'vertical', 'horizontal']"
    :bold-headers="false"
    :row-hover="false"
    theme="grey"
    :striped-rows="false"
  >
    <template #thead>
      <hlx-table-head
        v-for="(i, index) in treeAttributes"
        :key="index"
        :width="i.width"
        >{{ i.label }}</hlx-table-head
      >
    </template>
    <template #tbody>
      <tr v-for="(i, index) in table_data" id="" :key="index">
        <hlx-table-cell
          v-for="(j, col_index) in treeAttributes"
          :key="col_index"
          :align="'left'"
        >
          {{ i[j.prop] }}
        </hlx-table-cell>
      </tr>
    </template>
  </hlx-table>
  <h2 class="component-side-heading">Events</h2>
  <hlx-table
    :border="['table', 'header', 'vertical', 'horizontal']"
    :bold-headers="false"
    :row-hover="false"
    theme="grey"
    :striped-rows="false"
    :column-count="3"
  >
    <template #thead>
      <hlx-table-head
        v-for="(i, index) in events"
        :key="index"
        :width="i.width"
        >{{ i.label }}</hlx-table-head
      >
    </template>
    <template #tbody>
      <tr v-for="(i, index) in event_data" id="" :key="index">
        <hlx-table-cell
          v-for="(j, col_index) in events"
          :key="col_index"
          :align="'left'"
        >
          {{ i[j.prop] }}
        </hlx-table-cell>
      </tr>
    </template>
  </hlx-table>
  <hlx-divider :space="'20px'" class="mobile-nav-divider" />
  <div class="mobile-next-page-nav">
    <span class="prev-btn" @click="prevNextPage('prev', 'tabs')"
      ><i class="icon-angle-left-regular prev-icon"></i>Tab</span
    >
    <span class="nxt-btn" @click="prevNextPage('next', 'speeddial')"
      >Speed dial <i class="icon-angle-right-regular"></i
    ></span>
  </div>
</template>
<script>
import hlxTree from "../components/TreeComponent.vue";
import CodeEditor from "simple-code-editor";
import hlxTreeV2 from "../components/TreeVersion2Component.vue";
import axios from "axios";
import hlxButton from "../components/ButtonComponent.vue";
import hlxTable from "../components/table/HlxTable.vue";
import hlxTableHead from "../components/table/HlxTableHead.vue";
import hlxTableCell from "../components/table/HlxTableCell.vue";
import hlxDivider from "../components/DividerComponent.vue";
export default {
  name: "TreeView",
  components: {
    hlxTreeV2,
    hlxTree,
    hlxTableCell,
    hlxTableHead,
    hlxTable,
    hlxButton,
    CodeEditor,
    hlxDivider,
  },
  data() {
    return {
      event_data: [
        {
          name: "updated-data",
          description: "emits the tree data",
          parameter: "",
        },
        {
          name: "card-nodes",
          description: "emits the tree card data",
          parameter: "",
        },
        {
          name: "context",
          description: "emits the tree context menu value",
          parameter: "",
        },
        {
          name: "zoomIn",
          description: "For zoomIn the tree",
          parameter: "",
        },
        {
          name: "zoomOut",
          description: "For zoomOut the tree",
          parameter: "",
        },
      ],
      events: [
        {
          prop: "name",
          label: "Event name",
          width: 100,
          type: "string",
          format: "",
        },
        {
          prop: "description",
          label: "Description",
          width: 350,
          type: "string",
          format: "",
        },
        {
          prop: "parameter",
          label: "Parameter",
          width: 100,
          type: "string",
          format: "",
        },
      ],
      configValue: {
        url: "https://tmf-upc-ghubgwxrcq-pd.a.run.app/tree",
        key: "dbee69aa047d12f22275e281ee432da3:2920ca58abb53b122a1e06c94ceabdc1323d441d980a1f19d1c7f040a5e8910af798fdaf8a7213898efbbb43b74da0e3670964b9d96a4081415cca3beeed7727",
      },
      treedataval: {},
      treedata_product: `
<template>
<hlx-tree-v2
        :nodes="treedataProduct"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="product"
        :readOnly="false"
        :isDraggable="true"
        :config="configValue"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
 
</template>

<script>
export default {
data(){
  return{
    treedataProduct: {
        service: {
          icones: "icon-box-check-regular tree-icone3",
          name: "service",
          value: "service",
        },
        specification: {
          name: "all product",
        },
      },
  }
}
methods:{
     this.zoom += 10;
    },
    zoomout() {
      this.zoom -= 10;
    },
}
}
</\script>`,
      treedata_service_cfs: `
<template>
<hlx-tree-v2
        :nodes="treedataServiceCfs"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'service'"
        :config="configValue"
        :readOnly="false"
        :isDraggable="true"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
 
</template>

<script>
export default {
data(){
  return{
    treedataServiceCfs: {
        service: {
          icones: "icon-credit-card-regular tree-icone2",
          name: "Customer Facing Service",
          value: "Customer Facing Service",
        },
        specification: {
          name: "all product",
        },
      },
  }
}
methods:{
     this.zoom += 10;
    },
    zoomout() {
      this.zoom -= 10;
    },
}
}
</\script>`,
      treedata_service_rfs: `
<template>
<hlx-tree-v2
        :nodes="treedataServiceRfs"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :config="configValue"
        :zoom="zoom"
        :type="'service'"
        :readOnly="false"
        :isDraggable="true"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
 
</template>

<script>
export default {
data(){
  return{
   treedataServiceRfs: {
        service: {
          icones: "icon-shield-bolt-regular tree-icon",
          name: "Resource Facing Service",
          value: "Resource Facing Service",
        },
        specification: {
          name: "all product",
        },
      },
  }
}
methods:{
     this.zoom += 10;
    },
    zoomout() {
      this.zoom -= 10;
    },
}
}
</\script>`,
      treedata_physical: `
<template>
<hlx-tree-v2
        :nodes="treedataPhysical"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'resource'"
        :readOnly="true"
        :isDraggable="true"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
 
</template>

<script>
export default {
data(){
  return{
   treedataPhysical: {
        service: {
          icones: "icon-sd-card-regular tree-icon-Physical",
          name: "Physical Resource",
          value: "Physical Resource",
        },
        specification: {
          name: "all product",
        },
      },
  }
}
methods:{
     this.zoom += 10;
    },
    zoomout() {
      this.zoom -= 10;
    },
}
}
</\script>`,
      treedata_logical: `
<template>
<hlx-tree-v2
        :nodes="treedataLogical"
        :parent-name="'all product'"
        :direction="tree_direction"
        :zoomable="true"
        :zoom="zoom"
        :type="'resource'"
        :readOnly="true"
        :isDraggable="true"
        :customer-facing-service-option="dropdown2data1"
        :resource-facing-service-option="dropdown2data2"
        :physical-resource-option="dropdown2data3"
        :logical-resource-option="dropdown2data4"
        @updated-data="dataReceived"
        @zoomIn="zoomin"
        @zoomOut="zoomout"
      />
 
</template>

<script>
export default {
data(){
  return{
   treedataLogical: {
        service: {
          icones: "icon-cpu-regular tree-icon-Logical",
          name: "Logical Resource",
          value: "Logical Resource",
        },
        specification: {
          name: "all product",
        },
      },
  }
}
methods:{
     this.zoom += 10;
    },
    zoomout() {
      this.zoom -= 10;
    },
}
}
</\script>`,
      service: false,
      type: "product",
      treedataProduct: {
        service: {
          icones: "icon-box-check-regular tree-icone3",
          name: "service",
          value: "service",
          data: {},
        },
        specification: {
          name: "all product",
        },
      },
      treedataLogical: {
        service: {
          icones: "icon-cpu-regular tree-icon-Logical",

          name: "Logical Resource",

          value: "Logical Resource",
          data: {},
        },
        specification: {
          name: "all product",
        },
      },
      treedataHierarchy:{
    "_id": "67b1fe9fdefd9f124a75d4ec",
    "id": "67b1fe9fdefd9f124a75d4ec",
    "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/v1/67b1fe9fdefd9f124a75d4ec",
    "displayID": "ORD-0153",
    "category": "MDU-Equipment Order",
    "description": "",
    "attachments": [],
    "expectedCompletionDate": "",
    "creationDate": "2025-02-16T15:05:03.691Z",
    "externalId": [],
    "externalReference": [],
    "quote": [],
    "priority": "1",
    "completionDate": null,
    "requestedCompletionDate": null,
    "requestedStartDate": null,
    "channel": [],
    "notes": [],
    "productOrderItem": [],
    "geographicAddress": [],
    "state": "Acknowledged",
    "orderTotalPrice": [],
    "payment": [],
    "relatedParty": [
        {
            "@baseType": "accountExecutive",
            "@type": "approver",
            "name": "Sylvain Laniel",
            "value": "Sylvain Laniel",
            "id": "676be5eb1f1bff72d2164f71",
            "email": "Laniel.Sylvain@gmail.com"
        },
        {
            "@type": "orderOwner",
            "name": "admin head halleyx",
            "value": "admin head halleyx",
            "id": "65d34902237a9575f79cf80e",
            "company": "developerHalleyx",
            "email": "jegawi9906@gitated.com"
        },
        {
            "@type": "orderCompany",
            "name": "Shaadi",
            "tradingName": "Shaadi",
            "id": "67ac3b2114cc30ee373bf615",
            "isNew": false
        },
        {
            "@type": "orderContact",
            "chooseContact": {
                "name": "Ziat Kiat",
                "id": "67ac3b1d14cc30ee373bf5d9",
                "company": "Shaadi"
            },
            "contactType": {
                "name": "Signing authority",
                "value": "Signing authority"
            },
            "title": "Ms.",
            "firstName": "Ziat",
            "lastName": "Kiat",
            "email": "test12@hsksngmail.com",
            "phoneNumber": "(323) 232-3232",
            "telephoneNumber": null,
            "preferredLanguage": "english",
            "name": "Ziat Kiat",
            "id": "67ac3b1d14cc30ee373bf5d9",
            "company": "Shaadi"
        },
        {
            "@type": "requesterCompany",
            "sameAsPropertyCompany": true,
            "name": "Shaadi",
            "tradingName": "Shaadi",
            "id": "67ac3b2114cc30ee373bf615"
        },
        {
            "@type": "requesterContact",
            "sameAsPropertyCompany": true,
            "companyName": {
                "name": "Shaadi",
                "tradingName": "Shaadi",
                "id": "67ac3b2114cc30ee373bf615"
            },
            "chooseContact": {
                "name": "Ziat Kiat",
                "id": "67ac3b1d14cc30ee373bf5d9",
                "company": "Shaadi"
            },
            "contactType": "Signing authority",
            "title": "Ms.",
            "firstName": "Ziat",
            "lastName": "Kiat",
            "email": "test12@hsksngmail.com",
            "phoneNumber": "(323) 232-3232",
            "telephoneNumber": null,
            "preferredLanguage": "English",
            "name": "Ziat Kiat",
            "id": "67ac3b1d14cc30ee373bf5d9",
            "company": "Shaadi"
        },
        {
            "@type": "constructionCompany",
            "sameAsPropertyCompany": true,
            "name": "Shaadi",
            "tradingName": "Shaadi",
            "id": "67ac3b2114cc30ee373bf615"
        },
        {
            "@type": "constructionContact",
            "sameAsPropertyCompany": true,
            "companyName": {
                "name": "Shaadi",
                "tradingName": "Shaadi",
                "id": "67ac3b2114cc30ee373bf615"
            },
            "chooseContact": {
                "name": "Ziat Kiat",
                "id": "67ac3b1d14cc30ee373bf5d9",
                "company": "Shaadi"
            },
            "contactType": "Signing authority",
            "title": "Ms.",
            "firstName": "Ziat",
            "lastName": "Kiat",
            "email": "test12@hsksngmail.com",
            "phoneNumber": "(323) 232-3232",
            "telephoneNumber": null,
            "preferredLanguage": "English",
            "name": "Ziat Kiat",
            "id": "67ac3b1d14cc30ee373bf5d9",
            "company": "Shaadi"
        },
        {
            "@type": "site",
            "contactMedium": [
                {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "mediumType": "postalAddress",
                        "characteristic": {
                            "city": "York",
                            "unit": "120",
                            "country": "Canada",
                            "state": "Ontario",
                            "postalCode": "M6C 2M2",
                            "stateOrProvince": "Ontario",
                            "street1": "",
                            "street2": "",
                            "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                            "postalCodePrefix": "M6C",
                            "street": "12-120 Vaughan Rd",
                            "default": true,
                            "latitude": 43.6937813,
                            "longitude": -79.4281914,
                            "fsa": "M6C",
                            "@type": "",
                            "numberOfUnits": "",
                            "stateCode": "ON"
                        }
                    }
                }
            ],
            "numberOfUnits": 90,
            "siteName": "123 Maple Street",
            "siteAddress": {
                "mediumType": "postalAddress",
                "characteristic": {
                    "city": "York",
                    "unit": "120",
                    "country": "Canada",
                    "state": "Ontario",
                    "postalCode": "M6C 2M2",
                    "stateOrProvince": "Ontario",
                    "street1": "",
                    "street2": "",
                    "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                    "postalCodePrefix": "M6C",
                    "street": "12-120 Vaughan Rd",
                    "default": true,
                    "latitude": 43.6937813,
                    "longitude": -79.4281914,
                    "fsa": "M6C",
                    "@type": "",
                    "numberOfUnits": "",
                    "stateCode": "ON"
                }
            },
            "marketSegment": "Brownfield",
            "id": "67adaa627aedcd145aef9cb7",
            "name": "123 Maple Street"
        },
        {
            "@type": "site",
            "contactMedium": [
                {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "mediumType": "postalAddress",
                        "characteristic": {
                            "city": "Oakville",
                            "unit": "1489",
                            "country": "Canada",
                            "state": "Ontario",
                            "postalCode": "L6M 4M6",
                            "stateOrProvince": "Ontario",
                            "street1": "",
                            "street2": "",
                            "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                            "postalCodePrefix": "L6M",
                            "street": "21-1489 Heritage Way",
                            "default": true,
                            "latitude": 43.4377177,
                            "longitude": -79.77363059999999,
                            "fsa": "L6M",
                            "@type": "",
                            "numberOfUnits": "",
                            "stateCode": "ON"
                        }
                    }
                }
            ],
            "numberOfUnits": 185,
            "siteName": "10QT",
            "siteAddress": {
                "mediumType": "postalAddress",
                "characteristic": {
                    "city": "Oakville",
                    "unit": "1489",
                    "country": "Canada",
                    "state": "Ontario",
                    "postalCode": "L6M 4M6",
                    "stateOrProvince": "Ontario",
                    "street1": "",
                    "street2": "",
                    "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                    "postalCodePrefix": "L6M",
                    "street": "21-1489 Heritage Way",
                    "default": true,
                    "latitude": 43.4377177,
                    "longitude": -79.77363059999999,
                    "fsa": "L6M",
                    "@type": "",
                    "numberOfUnits": "",
                    "stateCode": "ON"
                }
            },
            "marketSegment": "Greenfield",
            "id": "67a4e1a7e578933ad6b0a39e",
            "name": "10QT"
        }
    ],
    "lastUpdate": "2025-02-16T14:26:12.268Z",
    "@type": "parentOrder",
    "@baseType": "salesOrder",
    "createdBy": "65d34902237a9575f79cf80e",
    "note": [],
    "status": "Open",
    "dwellingtype": "MDU Condo",
    "orderType": "New",
    "numberOfSites": 2,
    "isMajorRenovation": "Yes",
    "isNewMDU": "Yes",
    "isPlenumVentilationUsed": "Yes",
    "orderDate": "2025-02-16T15:05:03.691Z",
    "contactMedium": [
        {
            "characteristic": {
                "@type": "",
                "address1": "18 King St E",
                "city": "Montréal",
                "country": "Canada",
                "description": "1 Carrefour Alexandre Graham Bell, Building A-7, Verdun, Montréal, Quebec, Canada, H3E 3B3",
                "postCode": "",
                "postalCode": "H3E 3B3",
                "state": "Quebec",
                "stateOrProvince": "Quebec",
                "street1": "",
                "street2": "",
                "timezone": "",
                "unit": "",
                "postalCodePrefix": "H3E",
                "street": "1 Carrefour Alexandre Graham Bell",
                "stateCode": "QC",
                "longitude": -73.5412738,
                "latitude": 45.4721155,
                "fsa": "M5V"
            },
            "mediumType": "billingAddress"
        }
    ],
    "approvals": [
        {
            "site": {
                "name": "123 Maple Street",
                "siteName": "123 Maple Street",
                "id": "67adaa627aedcd145aef9cb7",
                "siteAddress": {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "city": "York",
                        "unit": "120",
                        "country": "Canada",
                        "state": "Ontario",
                        "postalCode": "M6C 2M2",
                        "stateOrProvince": "Ontario",
                        "street1": "",
                        "street2": "",
                        "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                        "postalCodePrefix": "M6C",
                        "street": "12-120 Vaughan Rd",
                        "default": true,
                        "latitude": 43.6937813,
                        "longitude": -79.4281914,
                        "fsa": "M6C",
                        "@type": "",
                        "numberOfUnits": "",
                        "stateCode": "ON"
                    }
                }
            },
            "name": "Sylvain Laniel",
            "value": "Sylvain Laniel",
            "id": "676be5eb1f1bff72d2164f71",
            "email": "Laniel.Sylvain@gmail.com",
            "@baseType": "accountExecutive",
            "@type": "approver",
            "status": "inProgress"
        },
        {
            "site": {
                "name": "123 Maple Street",
                "siteName": "123 Maple Street",
                "id": "67adaa627aedcd145aef9cb7",
                "siteAddress": {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "city": "York",
                        "unit": "120",
                        "country": "Canada",
                        "state": "Ontario",
                        "postalCode": "M6C 2M2",
                        "stateOrProvince": "Ontario",
                        "street1": "",
                        "street2": "",
                        "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                        "postalCodePrefix": "M6C",
                        "street": "12-120 Vaughan Rd",
                        "default": true,
                        "latitude": 43.6937813,
                        "longitude": -79.4281914,
                        "fsa": "M6C",
                        "@type": "",
                        "numberOfUnits": "",
                        "stateCode": "ON"
                    }
                }
            },
            "status": "inProgress"
        },
        {
            "site": {
                "name": "10QT",
                "siteName": "10QT",
                "id": "67a4e1a7e578933ad6b0a39e",
                "siteAddress": {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "city": "Oakville",
                        "unit": "1489",
                        "country": "Canada",
                        "state": "Ontario",
                        "postalCode": "L6M 4M6",
                        "stateOrProvince": "Ontario",
                        "street1": "",
                        "street2": "",
                        "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                        "postalCodePrefix": "L6M",
                        "street": "21-1489 Heritage Way",
                        "default": true,
                        "latitude": 43.4377177,
                        "longitude": -79.77363059999999,
                        "fsa": "L6M",
                        "@type": "",
                        "numberOfUnits": "",
                        "stateCode": "ON"
                    }
                }
            },
            "name": "Sylvain Laniel",
            "value": "Sylvain Laniel",
            "id": "676be5eb1f1bff72d2164f71",
            "email": "Laniel.Sylvain@gmail.com",
            "@baseType": "accountExecutive",
            "@type": "approver",
            "status": "inProgress"
        },
        {
            "site": {
                "name": "10QT",
                "siteName": "10QT",
                "id": "67a4e1a7e578933ad6b0a39e",
                "siteAddress": {
                    "mediumType": "postalAddress",
                    "characteristic": {
                        "city": "Oakville",
                        "unit": "1489",
                        "country": "Canada",
                        "state": "Ontario",
                        "postalCode": "L6M 4M6",
                        "stateOrProvince": "Ontario",
                        "street1": "",
                        "street2": "",
                        "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                        "postalCodePrefix": "L6M",
                        "street": "21-1489 Heritage Way",
                        "default": true,
                        "latitude": 43.4377177,
                        "longitude": -79.77363059999999,
                        "fsa": "L6M",
                        "@type": "",
                        "numberOfUnits": "",
                        "stateCode": "ON"
                    }
                }
            },
            "status": "inProgress"
        }
    ],
    "installation": [
        {
            "chooseSite": {
                "name": "123 Maple Street",
                "siteName": "123 Maple Street",
                "id": "67adaa627aedcd145aef9cb7"
            },
            "siteAddress": {
                "mediumType": "postalAddress",
                "characteristic": {
                    "city": "York",
                    "unit": "120",
                    "country": "Canada",
                    "state": "Ontario",
                    "postalCode": "M6C 2M2",
                    "stateOrProvince": "Ontario",
                    "street1": "",
                    "street2": "",
                    "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                    "postalCodePrefix": "M6C",
                    "street": "12-120 Vaughan Rd",
                    "default": true,
                    "latitude": 43.6937813,
                    "longitude": -79.4281914,
                    "fsa": "M6C",
                    "@type": "",
                    "numberOfUnits": "",
                    "stateCode": "ON"
                }
            },
            "status": "Pending"
        },
        {
            "chooseSite": {
                "name": "10QT",
                "siteName": "10QT",
                "id": "67a4e1a7e578933ad6b0a39e"
            },
            "siteAddress": {
                "mediumType": "postalAddress",
                "characteristic": {
                    "city": "Oakville",
                    "unit": "1489",
                    "country": "Canada",
                    "state": "Ontario",
                    "postalCode": "L6M 4M6",
                    "stateOrProvince": "Ontario",
                    "street1": "",
                    "street2": "",
                    "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                    "postalCodePrefix": "L6M",
                    "street": "21-1489 Heritage Way",
                    "default": true,
                    "latitude": 43.4377177,
                    "longitude": -79.77363059999999,
                    "fsa": "L6M",
                    "@type": "",
                    "numberOfUnits": "",
                    "stateCode": "ON"
                }
            },
            "status": "Pending"
        }
    ],
    "shipment": [],
    "createdAt": "2025-02-16T15:05:03.694Z",
    "updatedAt": "2025-02-16T15:05:04.062Z",
    "__v": 0,
    "orderRelationship": [
        {
            "id": "67b1fea0defd9f124a75d4fb",
            "referredType": "subOrder",
            "relationshipType": "child",
            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/v1/67b1fea0defd9f124a75d4fb",
            "category": "MDU-Equipment Order"
        },
        {
            "id": "67b1fea0defd9f124a75d4fe",
            "referredType": "subOrder",
            "relationshipType": "child",
            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/v1/67b1fea0defd9f124a75d4fe",
            "category": "MDU-Equipment Order"
        }
    ],
    "child": [
        {
            "_id": "67b1fea0defd9f124a75d4fb",
            "id": "67b1fea0defd9f124a75d4fb",
            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/v1/67b1fea0defd9f124a75d4fb",
            "displayID": "SUB-0217",
            "category": "MDU-Equipment Order",
            "attachments": [],
            "creationDate": "2025-02-16T15:05:03.773Z",
            "externalId": [
                {
                    "@type": "orderTree",
                    "data": {}
                }
            ],
            "externalReference": [],
            "quote": [],
            "priority": "1",
            "completionDate": null,
            "requestedCompletionDate": null,
            "requestedStartDate": null,
            "channel": [],
            "notes": [],
            "productOrderItem": [
                {
                    "offerId": "67894660f6d640ef4f572d8d",
                    "system": true,
                    "offerType": "Atomic",
                    "section": false,
                    "address": false,
                    "custom": false,
                    "notes": "",
                    "product_offering_name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                    "description": "",
                    "unitOfMeasure": "Each",
                    "additionalCharges": [],
                    "@type": "QuoteItem",
                    "productSpecification": {
                        "name": "Enclosure p3000",
                        "id": "6789432df6d640ef4f57283c",
                        "_id": "6789432df6d640ef4f57283c",
                        "category": [
                            "Electronics",
                            "Routers"
                        ],
                        "validFor": {
                            "endOfLife": "2025-01-31T00:00:00.000Z",
                            "endOfDate": "2025-01-31T00:00:00.000Z"
                        },
                        "@baseType": "Tangible",
                        "@type": "ProductSpecification"
                    },
                    "productOffering": {
                        "name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                        "id": "67894660f6d640ef4f572d8d",
                        "@type": "ProductOffering",
                        "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                    },
                    "productCharacteristic": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValueUse": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValue": [
                        {
                            "id": "3567e3c7-8667-400e-864a-15d8caf6518c",
                            "default": true,
                            "validFor": {
                                "startDate": "2025-01-16T17:48:20.592Z",
                                "endDate": "9999-12-31T00:00:00Z"
                            },
                            "priceType": "One time",
                            "tiers": [
                                {
                                    "min": 1,
                                    "max": 99,
                                    "price": {
                                        "unit": "CAD",
                                        "value": 99
                                    }
                                }
                            ],
                            "SKU": "04-992719NM",
                            "MSRP": {
                                "unit": "CAD",
                                "value": "99"
                            },
                            "lifecycleStatus": "Active",
                            "commitmentLength": 1,
                            "billingPeriod": "Monthly",
                            "displayValue": "Months",
                            "endOfCommitment": {
                                "autoPay": true,
                                "expireTerm": false
                            }
                        }
                    ],
                    "supplierUnitCost": 0,
                    "unitMsrp": 0,
                    "skuid": "3567e3c7-8667-400e-864a-15d8caf6518c",
                    "rebate": 0,
                    "action": "new",
                    "quantity": 185,
                    "duration": 1,
                    "billing_period_options": [
                        {
                            "name": "Monthly",
                            "value": "Monthly"
                        }
                    ],
                    "billing_period": "One time",
                    "priceType": "One time",
                    "discount": {
                        "value": 0,
                        "type": "%"
                    },
                    "state": "",
                    "product": {
                        "@type": "product",
                        "productSpecification": {
                            "name": "Enclosure p3000",
                            "id": "6789432df6d640ef4f57283c",
                            "_id": "6789432df6d640ef4f57283c",
                            "category": [
                                "Electronics",
                                "Routers"
                            ],
                            "validFor": {
                                "endOfLife": "2025-01-31T00:00:00.000Z",
                                "endOfDate": "2025-01-31T00:00:00.000Z"
                            },
                            "@baseType": "Tangible",
                            "@type": "ProductSpecification"
                        },
                        "productOffering": {
                            "name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                            "id": "67894660f6d640ef4f572d8d",
                            "@type": "ProductOffering",
                            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                        },
                        "productCharacteristic": [
                            {
                                "type": "Rectangular select",
                                "valueType": "String",
                                "default": {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                },
                                "configurable": false,
                                "productSpecCharacteristicValue": [
                                    {
                                        "value": "Titanium",
                                        "valueSuffix": ""
                                    }
                                ],
                                "name": "Material",
                                "context": false
                            }
                        ]
                    },
                    "quoteItemPrice": [
                        {
                            "description": "",
                            "name": "",
                            "priceType": "One time",
                            "recurringChargePeriodLength": 0,
                            "recurringChargePeriod": "One time",
                            "price": {
                                "taxRate": 0,
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": "3.00"
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 555
                                }
                            }
                        }
                    ],
                    "id": "1"
                },
                {
                    "offerId": "678946baf6d640ef4f573043",
                    "system": true,
                    "offerType": "Atomic",
                    "section": false,
                    "address": false,
                    "custom": false,
                    "notes": "",
                    "product_offering_name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                    "description": "",
                    "unitOfMeasure": "Each",
                    "additionalCharges": [],
                    "@type": "QuoteItem",
                    "productSpecification": {
                        "name": "Enclosure p3000",
                        "id": "6789432df6d640ef4f57283c",
                        "_id": "6789432df6d640ef4f57283c",
                        "category": [
                            "Electronics",
                            "Routers"
                        ],
                        "validFor": {
                            "endOfLife": "2025-01-31T00:00:00.000Z",
                            "endOfDate": "2025-01-31T00:00:00.000Z"
                        },
                        "@baseType": "Tangible",
                        "@type": "ProductSpecification"
                    },
                    "productOffering": {
                        "name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                        "id": "678946baf6d640ef4f573043",
                        "@type": "ProductOffering",
                        "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                    },
                    "productCharacteristic": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValueUse": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValue": [
                        {
                            "id": "f7912df2-6374-4f67-9419-6527dac63a9c",
                            "default": true,
                            "validFor": {
                                "startDate": "2025-01-16T17:49:51.488Z",
                                "endDate": "9999-12-31T00:00:00Z"
                            },
                            "priceType": "One time",
                            "tiers": [
                                {
                                    "min": 1,
                                    "max": 99,
                                    "price": {
                                        "unit": "CAD",
                                        "value": 35
                                    }
                                }
                            ],
                            "SKU": "04-992720NM",
                            "MSRP": {
                                "unit": "CAD",
                                "value": "35"
                            },
                            "lifecycleStatus": "Active",
                            "commitmentLength": 1,
                            "billingPeriod": "Monthly",
                            "displayValue": "Months",
                            "endOfCommitment": {
                                "autoPay": true,
                                "expireTerm": false
                            }
                        }
                    ],
                    "supplierUnitCost": 0,
                    "unitMsrp": 0,
                    "skuid": "f7912df2-6374-4f67-9419-6527dac63a9c",
                    "rebate": 0,
                    "action": "new",
                    "quantity": 185,
                    "duration": 1,
                    "billing_period_options": [
                        {
                            "name": "Monthly",
                            "value": "Monthly"
                        }
                    ],
                    "billing_period": "One time",
                    "priceType": "One time",
                    "discount": {
                        "value": 0,
                        "type": "%"
                    },
                    "state": "",
                    "product": {
                        "@type": "product",
                        "productSpecification": {
                            "name": "Enclosure p3000",
                            "id": "6789432df6d640ef4f57283c",
                            "_id": "6789432df6d640ef4f57283c",
                            "category": [
                                "Electronics",
                                "Routers"
                            ],
                            "validFor": {
                                "endOfLife": "2025-01-31T00:00:00.000Z",
                                "endOfDate": "2025-01-31T00:00:00.000Z"
                            },
                            "@baseType": "Tangible",
                            "@type": "ProductSpecification"
                        },
                        "productOffering": {
                            "name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                            "id": "678946baf6d640ef4f573043",
                            "@type": "ProductOffering",
                            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                        },
                        "productCharacteristic": [
                            {
                                "type": "Rectangular select",
                                "valueType": "String",
                                "default": {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                },
                                "configurable": false,
                                "productSpecCharacteristicValue": [
                                    {
                                        "value": "Titanium",
                                        "valueSuffix": ""
                                    }
                                ],
                                "name": "Material",
                                "context": false
                            }
                        ]
                    },
                    "quoteItemPrice": [
                        {
                            "description": "",
                            "name": "",
                            "priceType": "One time",
                            "recurringChargePeriodLength": 0,
                            "recurringChargePeriod": "One time",
                            "price": {
                                "taxRate": 0,
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": "3.00"
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 555
                                }
                            }
                        }
                    ],
                    "id": "2"
                }
            ],
            "geographicAddress": [],
            "state": "Acknowledged",
            "orderTotalPrice": [
                {
                    "quoteTotalPrice": [
                        {
                            "name": "One-time charges",
                            "priceType": "Onetime",
                            "price": {
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": 1110
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 1110
                                }
                            },
                            "priceAlteration": [],
                            "quoteTotal": {
                                "unit": "CAD",
                                "value": 1110
                            }
                        },
                        {
                            "name": "Recurring charges",
                            "priceType": "Recurring",
                            "price": {
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": 0
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 0
                                }
                            },
                            "priceAlteration": [],
                            "quoteTotal": {
                                "unit": "CAD",
                                "value": 0
                            }
                        },
                        {
                            "name": "Total contract value",
                            "@type": "totalContractValue",
                            "price": {
                                "unit": "CAD",
                                "value": 1110
                            }
                        }
                    ],
                    "quotePrice": {
                        "price": {
                            "dutyFreeAmount": {
                                "unit": "CAD",
                                "value": 1110
                            },
                            "taxIncludedAmount": {
                                "unit": "CAD",
                                "value": 1110
                            }
                        },
                        "quoteTotal": {
                            "unit": "CAD",
                            "value": 1110
                        },
                        "savings": {
                            "unit": "CAD",
                            "value": 0
                        },
                        "totalContractValue": {
                            "unit": "CAD",
                            "value": 1110
                        },
                        "additionalCharges": [],
                        "summary": {
                            "showOneTimeSummary": true,
                            "showRecurringSummary": false,
                            "priceable": true,
                            "oneTime": {
                                "subTotal": 1110,
                                "tax": 0,
                                "discount": [],
                                "item_wise_discount_onetime": true,
                                "amount": 1110,
                                "appliedTax": [],
                                "totalAfterTax": 1110,
                                "totalAfterDiscount": 1110
                            },
                            "recurring": {
                                "subTotal": 0,
                                "tax": 0,
                                "discount": [],
                                "item_wise_discount_recurring": true,
                                "amount": 0,
                                "appliedTax": [],
                                "totalAfterTax": 0,
                                "totalAfterDiscount": 0
                            },
                            "tax": [],
                            "additionalCharges": [],
                            "overAllTotal": 1110,
                            "amountSaved": 0,
                            "quoteTotalCost": 0,
                            "quoteMargin": "100.00",
                            "overAllTotalAfterTax": 1110,
                            "overAllTotalAfterTaxAfterDiscount": "1110.00",
                            "totalContractValue": 1110,
                            "totalContractValueAfterDiscount": 1110
                        }
                    }
                }
            ],
            "payment": [],
            "relatedParty": [
                {
                    "@baseType": "accountExecutive",
                    "@type": "approver",
                    "name": "Sylvain Laniel",
                    "value": "Sylvain Laniel",
                    "id": "676be5eb1f1bff72d2164f71",
                    "email": "Laniel.Sylvain@gmail.com"
                },
                {
                    "@type": "orderOwner",
                    "name": "admin head halleyx",
                    "value": "admin head halleyx",
                    "id": "65d34902237a9575f79cf80e",
                    "company": "developerHalleyx",
                    "email": "jegawi9906@gitated.com"
                },
                {
                    "@type": "orderCompany",
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615",
                    "isNew": false
                },
                {
                    "@type": "orderContact",
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": {
                        "name": "Signing authority",
                        "value": "Signing authority"
                    },
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "english",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "requesterCompany",
                    "sameAsPropertyCompany": true,
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615"
                },
                {
                    "@type": "requesterContact",
                    "sameAsPropertyCompany": true,
                    "companyName": {
                        "name": "Shaadi",
                        "tradingName": "Shaadi",
                        "id": "67ac3b2114cc30ee373bf615"
                    },
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": "Signing authority",
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "English",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "constructionCompany",
                    "sameAsPropertyCompany": true,
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615"
                },
                {
                    "@type": "constructionContact",
                    "sameAsPropertyCompany": true,
                    "companyName": {
                        "name": "Shaadi",
                        "tradingName": "Shaadi",
                        "id": "67ac3b2114cc30ee373bf615"
                    },
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": "Signing authority",
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "English",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "site",
                    "contactMedium": [
                        {
                            "mediumType": "postalAddress",
                            "characteristic": {
                                "mediumType": "postalAddress",
                                "characteristic": {
                                    "city": "Oakville",
                                    "unit": "1489",
                                    "country": "Canada",
                                    "state": "Ontario",
                                    "postalCode": "L6M 4M6",
                                    "stateOrProvince": "Ontario",
                                    "street1": "",
                                    "street2": "",
                                    "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                                    "postalCodePrefix": "L6M",
                                    "street": "21-1489 Heritage Way",
                                    "default": true,
                                    "latitude": 43.4377177,
                                    "longitude": -79.77363059999999,
                                    "fsa": "L6M",
                                    "@type": "",
                                    "numberOfUnits": "",
                                    "stateCode": "ON"
                                }
                            }
                        }
                    ],
                    "status": "Open",
                    "numberOfUnits": 185,
                    "siteName": "10QT",
                    "siteAddress": {
                        "mediumType": "postalAddress",
                        "characteristic": {
                            "city": "Oakville",
                            "unit": "1489",
                            "country": "Canada",
                            "state": "Ontario",
                            "postalCode": "L6M 4M6",
                            "stateOrProvince": "Ontario",
                            "street1": "",
                            "street2": "",
                            "description": "21-1489 Heritage Way, 1489, Oakville, Ontario, Canada, L6M 4M6",
                            "postalCodePrefix": "L6M",
                            "street": "21-1489 Heritage Way",
                            "default": true,
                            "latitude": 43.4377177,
                            "longitude": -79.77363059999999,
                            "fsa": "L6M",
                            "@type": "",
                            "numberOfUnits": "",
                            "stateCode": "ON"
                        }
                    },
                    "marketSegment": "Greenfield",
                    "id": "67a4e1a7e578933ad6b0a39e",
                    "name": "10QT"
                }
            ],
            "lastUpdate": "2025-02-16T14:26:12.268Z",
            "@type": "subOrder",
            "@baseType": "salesOrder",
            "createdBy": "65d34902237a9575f79cf80e",
            "isSubOrder": true,
            "status": "Open",
            "orderType": "New",
            "orderDate": "2025-02-16T15:05:03.774Z",
            "orderRelationship": [
                {
                    "id": "67b1fe9fdefd9f124a75d4ec",
                    "relationshipType": "parent"
                }
            ],
            "createdAt": "2025-02-16T15:05:04.051Z",
            "updatedAt": "2025-02-16T15:05:15.618Z",
            "__v": 0
        },
        {
            "_id": "67b1fea0defd9f124a75d4fe",
            "id": "67b1fea0defd9f124a75d4fe",
            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/v1/67b1fea0defd9f124a75d4fe",
            "displayID": "SUB-0217",
            "category": "MDU-Equipment Order",
            "attachments": [],
            "creationDate": "2025-02-16T15:05:03.773Z",
            "externalId": [
                {
                    "@type": "orderTree",
                    "data": {}
                }
            ],
            "externalReference": [],
            "quote": [],
            "priority": "1",
            "completionDate": null,
            "requestedCompletionDate": null,
            "requestedStartDate": null,
            "channel": [],
            "notes": [],
            "productOrderItem": [
                {
                    "offerId": "67894660f6d640ef4f572d8d",
                    "system": true,
                    "offerType": "Atomic",
                    "section": false,
                    "address": false,
                    "custom": false,
                    "notes": "",
                    "product_offering_name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                    "description": "",
                    "unitOfMeasure": "Each",
                    "additionalCharges": [],
                    "@type": "QuoteItem",
                    "productSpecification": {
                        "name": "Enclosure p3000",
                        "id": "6789432df6d640ef4f57283c",
                        "_id": "6789432df6d640ef4f57283c",
                        "category": [
                            "Electronics",
                            "Routers"
                        ],
                        "validFor": {
                            "endOfLife": "2025-01-31T00:00:00.000Z",
                            "endOfDate": "2025-01-31T00:00:00.000Z"
                        },
                        "@baseType": "Tangible",
                        "@type": "ProductSpecification"
                    },
                    "productOffering": {
                        "name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                        "id": "67894660f6d640ef4f572d8d",
                        "@type": "ProductOffering",
                        "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                    },
                    "productCharacteristic": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValueUse": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValue": [
                        {
                            "id": "3567e3c7-8667-400e-864a-15d8caf6518c",
                            "default": true,
                            "validFor": {
                                "startDate": "2025-01-16T17:48:20.592Z",
                                "endDate": "9999-12-31T00:00:00Z"
                            },
                            "priceType": "One time",
                            "tiers": [
                                {
                                    "min": 1,
                                    "max": 99,
                                    "price": {
                                        "unit": "CAD",
                                        "value": 99
                                    }
                                }
                            ],
                            "SKU": "04-992719NM",
                            "MSRP": {
                                "unit": "CAD",
                                "value": "99"
                            },
                            "lifecycleStatus": "Active",
                            "commitmentLength": 1,
                            "billingPeriod": "Monthly",
                            "displayValue": "Months",
                            "endOfCommitment": {
                                "autoPay": true,
                                "expireTerm": false
                            }
                        }
                    ],
                    "supplierUnitCost": 0,
                    "unitMsrp": 0,
                    "skuid": "3567e3c7-8667-400e-864a-15d8caf6518c",
                    "rebate": 0,
                    "action": "new",
                    "quantity": 90,
                    "duration": 1,
                    "billing_period_options": [
                        {
                            "name": "Monthly",
                            "value": "Monthly"
                        }
                    ],
                    "billing_period": "One time",
                    "priceType": "One time",
                    "discount": {
                        "value": 0,
                        "type": "%"
                    },
                    "state": "",
                    "product": {
                        "@type": "product",
                        "productSpecification": {
                            "name": "Enclosure p3000",
                            "id": "6789432df6d640ef4f57283c",
                            "_id": "6789432df6d640ef4f57283c",
                            "category": [
                                "Electronics",
                                "Routers"
                            ],
                            "validFor": {
                                "endOfLife": "2025-01-31T00:00:00.000Z",
                                "endOfDate": "2025-01-31T00:00:00.000Z"
                            },
                            "@baseType": "Tangible",
                            "@type": "ProductSpecification"
                        },
                        "productOffering": {
                            "name": "ENCLOSURE STRUCT WIRING P3000 14X30IN",
                            "id": "67894660f6d640ef4f572d8d",
                            "@type": "ProductOffering",
                            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                        },
                        "productCharacteristic": [
                            {
                                "type": "Rectangular select",
                                "valueType": "String",
                                "default": {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                },
                                "configurable": false,
                                "productSpecCharacteristicValue": [
                                    {
                                        "value": "Titanium",
                                        "valueSuffix": ""
                                    }
                                ],
                                "name": "Material",
                                "context": false
                            }
                        ]
                    },
                    "quoteItemPrice": [
                        {
                            "description": "",
                            "name": "",
                            "priceType": "One time",
                            "recurringChargePeriodLength": 0,
                            "recurringChargePeriod": "One time",
                            "price": {
                                "taxRate": 0,
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": "1.00"
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 90
                                }
                            }
                        }
                    ],
                    "id": "1"
                },
                {
                    "offerId": "678946baf6d640ef4f573043",
                    "system": true,
                    "offerType": "Atomic",
                    "section": false,
                    "address": false,
                    "custom": false,
                    "notes": "",
                    "product_offering_name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                    "description": "",
                    "unitOfMeasure": "Each",
                    "additionalCharges": [],
                    "@type": "QuoteItem",
                    "productSpecification": {
                        "name": "Enclosure p3000",
                        "id": "6789432df6d640ef4f57283c",
                        "_id": "6789432df6d640ef4f57283c",
                        "category": [
                            "Electronics",
                            "Routers"
                        ],
                        "validFor": {
                            "endOfLife": "2025-01-31T00:00:00.000Z",
                            "endOfDate": "2025-01-31T00:00:00.000Z"
                        },
                        "@baseType": "Tangible",
                        "@type": "ProductSpecification"
                    },
                    "productOffering": {
                        "name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                        "id": "678946baf6d640ef4f573043",
                        "@type": "ProductOffering",
                        "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                    },
                    "productCharacteristic": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValueUse": [
                        {
                            "type": "Rectangular select",
                            "valueType": "String",
                            "default": {
                                "value": "Titanium",
                                "valueSuffix": ""
                            },
                            "configurable": false,
                            "productSpecCharacteristicValue": [
                                {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                }
                            ],
                            "name": "Material",
                            "context": false
                        }
                    ],
                    "prodSpecCharValue": [
                        {
                            "id": "f7912df2-6374-4f67-9419-6527dac63a9c",
                            "default": true,
                            "validFor": {
                                "startDate": "2025-01-16T17:49:51.488Z",
                                "endDate": "9999-12-31T00:00:00Z"
                            },
                            "priceType": "One time",
                            "tiers": [
                                {
                                    "min": 1,
                                    "max": 99,
                                    "price": {
                                        "unit": "CAD",
                                        "value": 35
                                    }
                                }
                            ],
                            "SKU": "04-992720NM",
                            "MSRP": {
                                "unit": "CAD",
                                "value": "35"
                            },
                            "lifecycleStatus": "Active",
                            "commitmentLength": 1,
                            "billingPeriod": "Monthly",
                            "displayValue": "Months",
                            "endOfCommitment": {
                                "autoPay": true,
                                "expireTerm": false
                            }
                        }
                    ],
                    "supplierUnitCost": 0,
                    "unitMsrp": 0,
                    "skuid": "f7912df2-6374-4f67-9419-6527dac63a9c",
                    "rebate": 0,
                    "action": "new",
                    "quantity": 90,
                    "duration": 1,
                    "billing_period_options": [
                        {
                            "name": "Monthly",
                            "value": "Monthly"
                        }
                    ],
                    "billing_period": "One time",
                    "priceType": "One time",
                    "discount": {
                        "value": 0,
                        "type": "%"
                    },
                    "state": "",
                    "product": {
                        "@type": "product",
                        "productSpecification": {
                            "name": "Enclosure p3000",
                            "id": "6789432df6d640ef4f57283c",
                            "_id": "6789432df6d640ef4f57283c",
                            "category": [
                                "Electronics",
                                "Routers"
                            ],
                            "validFor": {
                                "endOfLife": "2025-01-31T00:00:00.000Z",
                                "endOfDate": "2025-01-31T00:00:00.000Z"
                            },
                            "@baseType": "Tangible",
                            "@type": "ProductSpecification"
                        },
                        "productOffering": {
                            "name": "DOOR FOR ENCLOSURE P3000 14X30IN",
                            "id": "678946baf6d640ef4f573043",
                            "@type": "ProductOffering",
                            "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering"
                        },
                        "productCharacteristic": [
                            {
                                "type": "Rectangular select",
                                "valueType": "String",
                                "default": {
                                    "value": "Titanium",
                                    "valueSuffix": ""
                                },
                                "configurable": false,
                                "productSpecCharacteristicValue": [
                                    {
                                        "value": "Titanium",
                                        "valueSuffix": ""
                                    }
                                ],
                                "name": "Material",
                                "context": false
                            }
                        ]
                    },
                    "quoteItemPrice": [
                        {
                            "description": "",
                            "name": "",
                            "priceType": "One time",
                            "recurringChargePeriodLength": 0,
                            "recurringChargePeriod": "One time",
                            "price": {
                                "taxRate": 0,
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": "2.00"
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 180
                                }
                            }
                        }
                    ],
                    "id": "2"
                }
            ],
            "geographicAddress": [],
            "state": "Acknowledged",
            "orderTotalPrice": [
                {
                    "quoteTotalPrice": [
                        {
                            "name": "One-time charges",
                            "priceType": "Onetime",
                            "price": {
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": 270
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 270
                                }
                            },
                            "priceAlteration": [],
                            "quoteTotal": {
                                "unit": "CAD",
                                "value": 270
                            }
                        },
                        {
                            "name": "Recurring charges",
                            "priceType": "Recurring",
                            "price": {
                                "dutyFreeAmount": {
                                    "unit": "CAD",
                                    "value": 0
                                },
                                "taxIncludedAmount": {
                                    "unit": "CAD",
                                    "value": 0
                                }
                            },
                            "priceAlteration": [],
                            "quoteTotal": {
                                "unit": "CAD",
                                "value": 0
                            }
                        },
                        {
                            "name": "Total contract value",
                            "@type": "totalContractValue",
                            "price": {
                                "unit": "CAD",
                                "value": 270
                            }
                        }
                    ],
                    "quotePrice": {
                        "price": {
                            "dutyFreeAmount": {
                                "unit": "CAD",
                                "value": 270
                            },
                            "taxIncludedAmount": {
                                "unit": "CAD",
                                "value": 270
                            }
                        },
                        "quoteTotal": {
                            "unit": "CAD",
                            "value": 270
                        },
                        "savings": {
                            "unit": "CAD",
                            "value": 0
                        },
                        "totalContractValue": {
                            "unit": "CAD",
                            "value": 270
                        },
                        "additionalCharges": [],
                        "summary": {
                            "showOneTimeSummary": true,
                            "showRecurringSummary": false,
                            "priceable": true,
                            "oneTime": {
                                "subTotal": 270,
                                "tax": 0,
                                "discount": [],
                                "item_wise_discount_onetime": true,
                                "amount": 270,
                                "appliedTax": [],
                                "totalAfterTax": 270,
                                "totalAfterDiscount": 270
                            },
                            "recurring": {
                                "subTotal": 0,
                                "tax": 0,
                                "discount": [],
                                "item_wise_discount_recurring": true,
                                "amount": 0,
                                "appliedTax": [],
                                "totalAfterTax": 0,
                                "totalAfterDiscount": 0
                            },
                            "tax": [],
                            "additionalCharges": [],
                            "overAllTotal": 270,
                            "amountSaved": 0,
                            "quoteTotalCost": 0,
                            "quoteMargin": "100.00",
                            "overAllTotalAfterTax": 270,
                            "overAllTotalAfterTaxAfterDiscount": "270.00",
                            "totalContractValue": 270,
                            "totalContractValueAfterDiscount": 270
                        }
                    }
                }
            ],
            "payment": [],
            "relatedParty": [
                {
                    "@baseType": "accountExecutive",
                    "@type": "approver",
                    "name": "Sylvain Laniel",
                    "value": "Sylvain Laniel",
                    "id": "676be5eb1f1bff72d2164f71",
                    "email": "Laniel.Sylvain@gmail.com"
                },
                {
                    "@type": "orderOwner",
                    "name": "admin head halleyx",
                    "value": "admin head halleyx",
                    "id": "65d34902237a9575f79cf80e",
                    "company": "developerHalleyx",
                    "email": "jegawi9906@gitated.com"
                },
                {
                    "@type": "orderCompany",
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615",
                    "isNew": false
                },
                {
                    "@type": "orderContact",
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": {
                        "name": "Signing authority",
                        "value": "Signing authority"
                    },
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "english",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "requesterCompany",
                    "sameAsPropertyCompany": true,
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615"
                },
                {
                    "@type": "requesterContact",
                    "sameAsPropertyCompany": true,
                    "companyName": {
                        "name": "Shaadi",
                        "tradingName": "Shaadi",
                        "id": "67ac3b2114cc30ee373bf615"
                    },
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": "Signing authority",
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "English",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "constructionCompany",
                    "sameAsPropertyCompany": true,
                    "name": "Shaadi",
                    "tradingName": "Shaadi",
                    "id": "67ac3b2114cc30ee373bf615"
                },
                {
                    "@type": "constructionContact",
                    "sameAsPropertyCompany": true,
                    "companyName": {
                        "name": "Shaadi",
                        "tradingName": "Shaadi",
                        "id": "67ac3b2114cc30ee373bf615"
                    },
                    "chooseContact": {
                        "name": "Ziat Kiat",
                        "id": "67ac3b1d14cc30ee373bf5d9",
                        "company": "Shaadi"
                    },
                    "contactType": "Signing authority",
                    "title": "Ms.",
                    "firstName": "Ziat",
                    "lastName": "Kiat",
                    "email": "test12@hsksngmail.com",
                    "phoneNumber": "(323) 232-3232",
                    "telephoneNumber": null,
                    "preferredLanguage": "English",
                    "name": "Ziat Kiat",
                    "id": "67ac3b1d14cc30ee373bf5d9",
                    "company": "Shaadi"
                },
                {
                    "@type": "site",
                    "contactMedium": [
                        {
                            "mediumType": "postalAddress",
                            "characteristic": {
                                "mediumType": "postalAddress",
                                "characteristic": {
                                    "city": "York",
                                    "unit": "120",
                                    "country": "Canada",
                                    "state": "Ontario",
                                    "postalCode": "M6C 2M2",
                                    "stateOrProvince": "Ontario",
                                    "street1": "",
                                    "street2": "",
                                    "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                                    "postalCodePrefix": "M6C",
                                    "street": "12-120 Vaughan Rd",
                                    "default": true,
                                    "latitude": 43.6937813,
                                    "longitude": -79.4281914,
                                    "fsa": "M6C",
                                    "@type": "",
                                    "numberOfUnits": "",
                                    "stateCode": "ON"
                                }
                            }
                        }
                    ],
                    "status": "Open",
                    "numberOfUnits": 90,
                    "siteName": "123 Maple Street",
                    "siteAddress": {
                        "mediumType": "postalAddress",
                        "characteristic": {
                            "city": "York",
                            "unit": "120",
                            "country": "Canada",
                            "state": "Ontario",
                            "postalCode": "M6C 2M2",
                            "stateOrProvince": "Ontario",
                            "street1": "",
                            "street2": "",
                            "description": "12-120 Vaughan Rd, 120, York, Ontario, Canada, M6C 2M2",
                            "postalCodePrefix": "M6C",
                            "street": "12-120 Vaughan Rd",
                            "default": true,
                            "latitude": 43.6937813,
                            "longitude": -79.4281914,
                            "fsa": "M6C",
                            "@type": "",
                            "numberOfUnits": "",
                            "stateCode": "ON"
                        }
                    },
                    "marketSegment": "Brownfield",
                    "id": "67adaa627aedcd145aef9cb7",
                    "name": "123 Maple Street"
                }
            ],
            "lastUpdate": "2025-02-16T14:26:12.268Z",
            "@type": "subOrder",
            "@baseType": "salesOrder",
            "createdBy": "65d34902237a9575f79cf80e",
            "isSubOrder": true,
            "status": "Open",
            "orderType": "New",
            "orderDate": "2025-02-16T15:05:03.773Z",
            "orderRelationship": [
                {
                    "id": "67b1fe9fdefd9f124a75d4ec",
                    "relationshipType": "parent"
                }
            ],
            "createdAt": "2025-02-16T15:05:04.054Z",
            "updatedAt": "2025-02-16T15:05:15.620Z",
            "__v": 0
        }
    ]
},
      treedataPhysical: {
        service: {
          icones: "icon-sd-card-regular tree-icon-Physical",

          name: "Physical Resource",

          value: "Physical Resource",
          data: {},
        },
        specification: {
          name: "all product",
        },
      },
      treedataServiceCfs: {
        service: {
          icones: "icon-users-group-alt-filled tree-icone2",
          name: "Customer Facing Service",
          value: "Customer Facing Service",
          data: {},
        },
        specification: {
          name: "all product",
        },
      },
      treedataServiceRfs: {
  "description": "BIS internet order Base 2",
  "id": "665f064a8cf9363cd3c5470c",
  "state": "acknowledged",
  "@type": "productOrder",
  "priority": "1",
  "orderItems": [
    {
      "quantity": 1,
      "action": "add",
      "product": {
        "isBundle": false,
        "@type": "UNI",
        "productSpecification": {
          "_id": "665c7e434378c6e37f5366b3",
          "id": "665c7e434378c6e37f5366b3",
          "name": "BIS Interner",
          "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productSpecification",
          "brand": "Ericsson",
          "isBundle": false
        },
        "productCharacteristic": [
          {
            "type": "Rectangular select",
            "valueType": "String",
            "default": null,
            "productSpecCharacteristicValue": [
              { "value": "Copper", "valueSuffix": "" },
              {
                "value": "Fiber",
                "id": "",
                "type": "string",
                "valueSuffix": ""
              }
            ],
            "name": "Access type",
            "configurable": true,
            "context": false
          }
        ]
      },
      "productOffering": {
        "_id": "665c7e7286dd40230bd9066e",
        "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/catalogManagement/productOffering",
        "id": "665c7e7286dd40230bd9066e",
        "lifecycleStatus": "Draft",
        "name": "BIS internet offer",
        "isBundle": false,
        "@type": "ProductOffering"
      },
      "orderItemPrice": [
        {
          "description": "Tariff plan 25 fee",
          "name": "Tariff Plan 25 tariff",
          "priceType": "recurring",
          "recurringChargePeriod": "month",
          "price": {
            "taxRate": 16,
            "dutyFreeAmount": { "unit": "EUR", "value": 30 },
            "taxIncludedAmount": { "unit": "EUR", "value": 34.8 }
          },
          "priceAlteration": [
            {
              "applicationDuration": 0,
              "description": "Specific Discount for PerfectCustomer company",
              "name": "10% off",
              "priceType": "recurring",
              "priority": 1,
              "recurringChargePeriod": "month",
              "price": { "percentage": 10 }
            }
          ]
        }
      ],
      "itemTerm": [
        {
          "description": "Tariff plan 12 Months commitment",
          "name": "12Months",
          "@type": "OrderTerm",
          "duration": { "amount": 12, "units": "month", "@type": "Duration" }
        }
      ],
      "state": "acknowledged",
      "@type": "ProductOrderItem",
      "id": "665f064f8cf9363cd3c5472a",
      "productOrderItemRelationship": [
        {
          "@relationshipType": "reliesOnChildOrder",
          "id": "665f064d8cf9363cd3c5471e",
          "state": "Acknowledged",
          "@type": "serviceOrder",
          "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/serviceOrder/665f064d8cf9363cd3c5471e"
        },
        {
          "@relationshipType": "reliesOnChildOrder",
          "state": "Acknowledged",
          "id": "665f06548cf9363cd3c54742",
          "@type": "resourceOrder",
          "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/resourceOrder/665f06548cf9363cd3c54742"
        }
      ]
    }
  ],
  "child": [
    {
      "description": "Service order of AccessCFS",
      "id": "665f064d8cf9363cd3c5471e",
      "state": "Acknowledged",
      "orderItems": [
        {
          "id": "665f064c8cf9363cd3c54717",
          "action": "add",
          "state": "acknowledged",
          "quantity": 1,
          "service": {
            "state": "active",
            "serviceType": null,
            "@type": "Service",
            "serviceRelationship": [],
            "serviceSpecification": {
              "name": "AccessCFS",
              "description": "",
              "category": [],
              "@type": "Customer Facing Service",
              "id": "661d7553823e6972b54d1d45"
            }
          },
          "serviceOrderItemRelationship": [
            {
              "@relationshipType": "parentOrder",
              "id": "665f064a8cf9363cd3c5470c",
              "@type": "ProductOrder",
              "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/665f064a8cf9363cd3c5470c"
            },
            {
              "@relationshipType": "reliesOnChildOrder",
              "state": "Acknowledged",
              "id": "665f06548cf9363cd3c5473f",
              "@type": "serviceOrder",
              "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/serviceOrder/665f06548cf9363cd3c5473f"
            },
            {
              "@relationshipType": "reliesOnChildOrder",
              "state": "Acknowledged",
              "id": "665f065a8cf9363cd3c54779",
              "@type": "resourceOrder",
              "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/resourceOrder/665f065a8cf9363cd3c54779"
            }
          ],
          "@type": "ServiceOrderItem"
        }
      ],
      "@type": "serviceOrder",
      "child": [
        {
          "description": "Service order of SiteAccessFiber",
          "id": "665f06548cf9363cd3c5473f",
          "state": "Acknowledged",
          "orderItems": [
            {
              "id": "665f06538cf9363cd3c54735",
              "action": "add",
              "state": "acknowledged",
              "quantity": 1,
              "service": {
                "state": "active",
                "serviceType": null,
                "@type": "Service",
                "serviceRelationship": [],
                "serviceSpecification": {
                  "name": "SiteAccessFiber",
                  "description": null,
                  "category": [],
                  "@type": "Resource Facing Service",
                  "id": null
                }
              },
              "serviceOrderItemRelationship": [
                {
                  "@relationshipType": "parentOrder",
                  "id": "665f064d8cf9363cd3c5471e",
                  "@type": "serviceOrder",
                  "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/serviceOrder/665f064d8cf9363cd3c5471e"
                },
                {
                  "@relationshipType": "reliesOnChildOrder",
                  "state": "Acknowledged",
                  "id": "665f065a8cf9363cd3c54776",
                  "@type": "resourceOrder",
                  "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/resourceOrder/665f065a8cf9363cd3c54776"
                }
              ],
              "@type": "ServiceOrderItem"
            }
          ],
          "@type": "serviceOrder",
          "child": [
            {
              "description": "Resource order of Service pointer",
              "id": "665f065a8cf9363cd3c54776",
              "@type": "resourceOrder",
              "orderItems": [
                {
                  "id": "665f06598cf9363cd3c54767",
                  "quantity": 1,
                  "action": "add",
                  "resource": {
                    "href": "",
                    "category": ["Connectivity services", "Internet"],
                    "name": "Service pointer",
                    "resourceVersion": "1.0",
                    "attachment": [],
                    "operationalState": "disable",
                    "relatedParty": [],
                    "resourceSpecification": {
                      "id": "664b0dcf1450076dd4f646c4",
                      "name": "Service pointer",
                      "category": ["Connectivity services", "Internet"],
                      "@type": "Logical Resource"
                    },
                    "@baseType": "Resource",
                    "@type": "Logical Resource",
                    "state": "acknowledged"
                  },
                  "resourceOrderItemRelationship": [
                    {
                      "@relationshipType": "parentOrder",
                      "id": "665f06548cf9363cd3c5473f",
                      "@type": "serviceOrder",
                      "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/serviceOrder/665f06548cf9363cd3c5473f"
                    }
                  ]
                }
              ],
              "state": "Acknowledged",
              "child": []
            }
          ]
        },
        {
          "description": "Resource order of Customer edge router",
          "id": "665f065a8cf9363cd3c54779",
          "@type": "resourceOrder",
          "orderItems": [
            {
              "id": "665f06538cf9363cd3c54736",
              "quantity": 1,
              "action": "add",
              "resource": {
                "href": "",
                "category": ["Devices", "Modem"],
                "description": "",
                "name": "Customer edge router",
                "resourceVersion": "1.0",
                "attachment": [],
                "operationalState": "disable",
                "relatedParty": [],
                "resourceSpecification": {
                  "id": "6620b1ba622e33d65d2f110b",
                  "name": "Customer edge router",
                  "description": "",
                  "category": ["Devices", "Modem"],
                  "@type": "Physical Resource"
                },
                "@baseType": "Resource",
                "@type": "Physical Resource",
                "state": "acknowledged"
              },
              "resourceOrderItemRelationship": [
                {
                  "@relationshipType": "parentOrder",
                  "id": "665f064d8cf9363cd3c5471e",
                  "@type": "serviceOrder",
                  "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/serviceOrder/665f064d8cf9363cd3c5471e"
                }
              ]
            }
          ],
          "state": "Acknowledged",
          "child": []
        }
      ]
    },
    {
      "description": "Resource order of device attachment",
      "id": "665f06548cf9363cd3c54742",
      "@type": "resourceOrder",
      "orderItems": [
        {
          "id": "665f064c8cf9363cd3c54718",
          "quantity": 1,
          "action": "add",
          "resource": {
            "href": "",
            "category": ["Devices", "Modem"],
            "name": "device attachment",
            "resourceVersion": "1.0",
            "attachment": [],
            "operationalState": "disable",
            "relatedParty": [],
            "resourceSpecification": {
              "id": "663ae4a2f884c9ec7a89803a",
              "name": "device attachment",
              "category": ["Devices", "Modem"],
              "@type": "Physical Resource"
            },
            "@baseType": "Resource",
            "@type": "Physical Resource",
            "state": "acknowledged"
          },
          "resourceOrderItemRelationship": [
            {
              "@relationshipType": "parentOrder",
              "id": "665f064a8cf9363cd3c5470c",
              "@type": "ProductOrder",
              "href": "https://tmf-upc-ghubgwxrcq-pd.a.run.app/productOrder/665f064a8cf9363cd3c5470c"
            }
          ]
        }
      ],
      "state": "Acknowledged",
      "child": []
    }
  ]
}
,
      dropdown2data1: [],
      // dropdown2data1: [
      //   {
      //     name: "IoT Soluvec Ltd",
      //     description:
      //       "This service catalog describes service that address the internet of things segmentThis service catalog describes service that address the internet of things segmentThis service catalog describes service that address the internet of things segmentThis service catalog describes service that address the internet of things segmentThis service catalog describes service that address the internet of things segment",
      //     lifecycleStatus: "Rejected",
      //     serviceSpecCharacteristic: [
      //       { name: "Resource Interaction " },
      //       { name: "Resource Abstraction" },
      //       { name: "Resource Lifecycle Management" },
      //     ],
      //   },
      //   {
      //     name: "E-commerce Website",
      //     description:
      //       "This service catalog entry represents our customer-facing e-commerce website.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Support (Email)",
      //     description:
      //       "This service catalog entry covers customer support via email for our clients.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Mobile App",
      //     description:
      //       "This service catalog entry represents our customer-facing mobile application.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "In-Person Consultation",
      //     description:
      //       "This service catalog entry includes in-person consultations with our clients.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Loyalty Program",
      //     description:
      //       "This service catalog entry outlines our customer-facing loyalty program.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Live Chat Support",
      //     description:
      //       "This service catalog entry provides customer support via live chat on our website.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Feedback Portal",
      //     description:
      //       "This service catalog entry covers our customer-facing feedback portal.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Social Media Engagement",
      //     description:
      //       "This service catalog entry represents our customer-facing social media engagement activities.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Online Booking System",
      //     description:
      //       "This service catalog entry covers our customer-facing online booking system.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Self-Serice Portal",
      //     description:
      //       "This service catalog entry represents our customer-facing self-service portal.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Product Demo Workshops",
      //     description:
      //       "This service catalog entry covers our customer-facing product demonstration workshops.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Virtual Tours",
      //     description:
      //       "This service catalog entry represents our customer-facing virtual tours of our facilities.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Webinars",
      //     description:
      //       "This service catalog entry covers our customer-facing educational webinars.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Product Customization",
      //     description:
      //       "This service catalog entry represents our customer-facing product customization options.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      //   {
      //     name: "Customer Onboarding",
      //     description:
      //       "This service catalog entry covers our customer-facing onboarding process for new clients.",
      //     lifecycleStatus: "Active",
      //     characteristics: [
      //       { name: "Resource Monitoring and Alerts" },
      //       { name: "Resource Performance Optimization" },
      //       { name: "Resource Documentation" },
      //     ],
      //   },
      // ],
      dropdown2data2: [
        {
          name: "Database Management",
          description:
            "This service catalog entry represents our resource-facing database management service.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Server Monitoring",
          description:
            "This service catalog entry covers our resource-facing server monitoring and maintenance.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Network Security",
          description:
            "This service catalog entry represents our resource-facing network security measures.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Data Backup and Recovery",
          description:
            "This service catalog entry covers resource-facing data backup and recovery processes.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Hardware Procurement",
          description:
            "This service catalog entry represents our resource-facing hardware procurement process.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Cloud Resource Allocation",
          description:
            "This service catalog entry covers resource-facing cloud resource allocation and management.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "IT Infrastructure Planning",
          description:
            "This service catalog entry represents our resource-facing IT infrastructure planning service.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "License Management",
          description:
            "This service catalog entry covers resource-facing software license management.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Data Center Operations",
          description:
            "This service catalog entry represents resource-facing data center operations and maintenance.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Vendor Management",
          description:
            "This service catalog entry covers resource-facing vendor and supplier management.",
          lifecycleStatus: "Inactive",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Capacity Planning",
          description:
            "This service catalog entry represents resource-facing capacity planning for IT resources.",
          lifecycleStatus: "Draft",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Network Configuration",
          description:
            "This service catalog entry covers resource-facing network configuration and optimization.",
          lifecycleStatus: "Pending",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Asset Inventory Management",
          description:
            "This service catalog entry represents resource-facing asset inventory tracking.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Software Patch Management",
          description:
            "This service catalog entry covers resource-facing software patching and updates.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Server Virtualization",
          description:
            "This service catalog entry represents resource-facing server virtualization and management.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
      ],
      dropdown2data3: [
        {
          name: "Silver Loyalty Program",
          description:
            "Exclusive benefits for loyal customers at the silver level.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Gold Rewards Membership",
          description: "Our premium loyalty program with gold-level benefits.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Bronze Loyalty Tier",
          description: "Entry-level loyalty program with bronze-tier perks.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Platinum Rewards Club",
          description:
            "Our top-tier loyalty program for platinum-level members.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Frequent Shopper Program",
          description:
            "Earn rewards for frequent shopping with our loyalty program.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Exclusive VIP Loyalty",
          description: "VIP loyalty program for exclusive customers.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Youth Loyalty Program",
          description:
            "Loyalty program designed for our younger customer base.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Family Rewards Membership",
          description:
            "Loyalty program with family-oriented benefits and offers.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Travel Enthusiast Program",
          description: "Loyalty program for customers passionate about travel.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Digital Rewards Club",
          description:
            "Loyalty program with digital-exclusive rewards and offers.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Sports Fan Loyalty",
          description:
            "Loyalty program for sports enthusiasts with exclusive sports-related benefits.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Green Eco Loyalty",
          description:
            "Loyalty program for environmentally conscious customers.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Tech Enthusiast Rewards",
          description:
            "Loyalty program for tech-savvy customers with tech-related rewards.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Health and Wellness Club",
          description:
            "Loyalty program promoting health and wellness with exclusive benefits.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Gourmet Foodie Loyalty",
          description:
            "Loyalty program for food enthusiasts with gourmet-related perks.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Art and Culture Rewards",
          description:
            "Loyalty program for customers interested in art and culture.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Pet Lover Loyalty",
          description: "Loyalty program for customers who love their pets.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Fashionista Rewards",
          description:
            "Loyalty program for fashion-forward customers with exclusive fashion benefits.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Bookworm Loyalty Club",
          description: "Loyalty program for avid readers and book enthusiasts.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
      ],
      dropdown2data4: [
        {
          name: "Adventure Seeker Rewards",
          description:
            "Loyalty program for customers seeking adventurous experiences.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Home Decor Enthusiast Club",
          description:
            "Loyalty program for customers passionate about home decor.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Outdoor Fitness Rewards",
          description:
            "Loyalty program for fitness enthusiasts who prefer outdoor activities.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "DIY Project Masters",
          description:
            "Loyalty program for customers who love do-it-yourself (DIY) projects.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Car Enthusiast Rewards",
          description:
            "Loyalty program for customers passionate about cars and automotive culture.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Tech and Gaming Elite",
          description:
            "Loyalty program for customers at the intersection of technology and gaming.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Culinary Connoisseur Club",
          description:
            "Loyalty program for customers with a passion for gourmet cuisine.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Wellness and Mindfulness Rewards",
          description:
            "Loyalty program promoting wellness and mindfulness practices.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Social Media Maven Club",
          description:
            "Loyalty program for customers who are influential on social media.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Smart Home Innovators",
          description:
            "Loyalty program for customers embracing smart home technologies.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Fitness Challenge Champions",
          description:
            "Loyalty program for customers participating in fitness challenges.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Travel Photography Enthusiasts",
          description:
            "Loyalty program for customers passionate about travel photography.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Nature Explorer Rewards",
          description:
            "Loyalty program for customers exploring and appreciating nature.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Fitness Tech Innovators",
          description:
            "Loyalty program for customers adopting cutting-edge fitness technologies.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Gaming Community Rewards",
          description:
            "Loyalty program for customers actively engaged in gaming communities.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Mindful Parenting Loyalty",
          description:
            "Loyalty program for parents dedicated to mindful parenting practices.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Virtual Reality Enthusiasts Club",
          description:
            "Loyalty program for customers exploring virtual reality experiences.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Green Thumb Rewards",
          description:
            "Loyalty program for gardening enthusiasts with green thumbs.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
        {
          name: "Educational Explorer Club",
          description:
            "Loyalty program for customers dedicated to continuous learning and education.",
          lifecycleStatus: "Active",
          characteristics: [
            { name: "Resource Monitoring and Alerts" },
            { name: "Resource Performance Optimization" },
            { name: "Resource Documentation" },
          ],
        },
      ],
      expandAll: false,
      collapse: false,
      isCollapsed: false,
      collapseAll: false,
      editor_theme: "light",
      tree_direction: "vertical",
      zoom: 100,
      table_data: [
        {
          attribute: "tree-direction",
          description: "To specify the direction of tree.",
          type: "String",
          accepted_values: "horizontal,vertical",
          default: "vertical",
          mandatory: false,
        },
        {
          attribute: "is-draggable",
          description: "To make tree draggable",
          type: "Boolean",
          accepted_values: "true,false",
          default: "false",
          mandatory: false,
        },
        {
          attribute: "expand-all",
          description: "To expand all nodess",
          type: "Boolean",
          accepted_values: "true,false",
          default: "false",
          mandatory: false,
        },
        {
          attribute: "collapse-all",
          description: "To collapse all nodess",
          type: "Boolean",
          accepted_values: "true,false",
          default: "false",
          mandatory: false,
        },
        {
          attribute: "zoomable",
          description: "To make tree zoomable",
          type: "Boolean",
          accepted_values: "true,false",
          default: "false",
          mandatory: false,
        },
        {
          attribute: "config",
          description:
            "Provied the Api key for the authentication and Provied the Api for adding child. ",
          type: "String",
          accepted_values: `
config:{
  url:"https://example/tree",
key:"dbee69aa047d12f22275e281ee432da3"
  }`,
          default: "-",
          mandatory: true,
        },
        {
          attribute: "nodes",
          description: `To build a tree data and format should be like 
         service: {
          icones: "icon-credit-card-regular ",
          name: "service",
          value: "service",
          data: {},
        },
        specification: {
          name: "all product",
        }`,
          type: "Object",
          accepted_values: "Object",
          default: "-",
          mandatory: true,
        },
        {
          attribute: "parent-name",
          description: `Pass the value of first nodes data specification name`,
          type: "Srting",
          accepted_values: "Srting",
          default: "-",
          mandatory: true,
        },
        {
          attribute: "type",
          description: `Pass the value of tree type`,
          type: "Srting",
          accepted_values: "product/service/resource/Hierarchy",
          default: "-",
          mandatory: true,
        },
        {
          attribute: "customer-facing-service-option",
          description: `Pass the list of items to be added in the customer-facing-service dropdown list`,
          type: "Array",
          accepted_values: "Array of objects",
          default: "-",
          mandatory: false,
        },
        {
          attribute: "resource-facing-service-option",
          description: `Pass the list of items to be added in the resource-facing-service-option dropdown list`,
          type: "Array",
          accepted_values: "Array of objects",
          default: "-",
          mandatory: false,
        },
        {
          attribute: "physical-resource-option",
          description: `Pass the list of items to be added in the physical-resource-option dropdown list`,
          type: "Array",
          accepted_values: "Array of objects",
          default: "-",
          mandatory: false,
        },
        {
          attribute: "logical-resource-option",
          description: `Pass the list of items to be added in the logical-resource-option dropdown list`,
          type: "Array",
          accepted_values: "Array of objects",
          default: "-",
          mandatory: false,
        },
        {
          attribute: "readOnly",
          description: `Disables user input`,
          type: "Boolean",
          accepted_values: "true/false",
          default: "false",
          mandatory: false,
        },
        {
          attribute: "zoom",
          description: "percentage of zoom level to achieve",
          type: "Number",
          accepted_values: ">=10 and <= 200",
          default: "100",
          mandatory: false,
        },
        {
          attribute: "nodes",
          description: `
          To build a tree data and format should be like 
          {
            label: String,
            data: Object,
            child: [
                {
                label: "String",
                data: Object,
                child: [{...}]
                }
              ]`,
          type: "Object",
          accepted_values: "Object",
          default: "-",
          mandatory: true,
        },
      ],
      treeAttributes: [
        {
          prop: "attribute",
          label: "Name",
          width: 150,
          type: "string",
          format: "",
        },
        {
          prop: "description",
          label: "Description",
          width: 250,
          type: "string",
          format: "",
        },
        {
          prop: "type",
          label: "Type",
          width: 50,
          type: "string",
          format: "",
        },
        {
          prop: "accepted_values",
          label: "Accepted values",
          width: 150,
          type: "string",
          format: "",
        },
        {
          prop: "default",
          label: "Default",
          width: 80,
          type: "string",
          format: "",
        },
        {
          prop: "mandatory",
          label: "Mandatory",
          width: 90,
          type: "string",
          format: "",
        },
      ],

      basic_tree: `
<template>
   <ul class="tree" :class="tree_direction">
                  <hlx-tree :nodes="treedata" :tree-direction="tree_direction"/>
      </ul>
</template>
<script>

export default {
  name: "TreeView",
  components: {
    TreeChild,
  },

  watch:{
      tree_direction : function(){
        this.tree_direction
      }
  },
  methods: {

    myFunction(){
      if(this.tree_direction=="vertical"){
        this.tree_direction="horizontal"
      }
      else{
        this.tree_direction="vertical"
      }
    }
  },
  data() {
    return {
    
      tree_direction: "vertical",
      treedata: {
        label: "root",
        child: [
          {
            label: "child1",
            child: [
              {
                label: "child1.2",
                child: [
                  {
                    label: "child1.2.1",
                    child: [
                      {
                        label: "child1.2.11",
                        child: [],
                      },
                    ],
                  },
                  {
                    label: "child1.2.2",
                    child: [],
                  },
                ],
              },
            ],
          },
          {
            label: "child2",
            child: [
              {
                label: "child2.1",
                child: [],
              },
            ],
          },
        ],
      },
    };
  },
};
<\/script>
<style lang="scss" scoped>
.example-body {
  position: relative;
  .tree-container {
    position: relative;
    height: auto;
    width: 100%;
    margin-left: auto;
    padding: 0 599.9px;
    /* border: solid; */
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.custom {
    height: 800px;
    overflow: auto;
  }
  .custom {
    position: sticky;
    left: 0%;
  }
}
.switcher {
  display: flex;
  gap: 10px;
}
.change-btn {
  float: right;
  border: none;
  color: white;
  padding: 8px;
  width: 100px;
  text-align: center;
  font-size: 14px;
  margin: 10px 10px;
  margin-left: auto;
  cursor: pointer;
}
</style>

      `,
      drag_tree: `
<template>
   <ul class="tree" :class="tree_direction">
                  <hlx-tree :nodes="treedata" :tree-direction="tree_direction" :isDraggable="true"/>
      </ul>
</template>
<script>

export default {
  name: "TreeView",
  components: {
    TreeChild,
  },

  watch:{
      tree_direction : function(){
        this.tree_direction
      }
  },
  methods: {

    myFunction(){
      if(this.tree_direction=="vertical"){
        this.tree_direction="horizontal"
      }
      else{
        this.tree_direction="vertical"
      }
    }
  },
  data() {
    return {
    
      tree_direction: "vertical",
      treedata: {
        label: "root",
        child: [
          {
            label: "child1",
            child: [
              {
                label: "child1.2",
                child: [
                  {
                    label: "child1.2.1",
                    child: [
                      {
                        label: "child1.2.11",
                        child: [],
                      },
                    ],
                  },
                  {
                    label: "child1.2.2",
                    child: [],
                  },
                ],
              },
            ],
          },
          {
            label: "child2",
            child: [
              {
                label: "child2.1",
                child: [],
              },
            ],
          },
        ],
      },
    };
  },
};
<\/script>
<style lang="scss" scoped>
.example-body {
  position: relative;
  .tree-container {
    position: relative;
    height: auto;
    width: 100%;
    margin-left: auto;
    padding: 0 599.9px;
    /* border: solid; */
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.custom {
    height: 800px;
    overflow: auto;
  }
  .custom {
    position: sticky;
    left: 0%;
  }
}
.switcher {
  display: flex;
  gap: 10px;
}
.change-btn {
  float: right;
  border: none;
  color: white;
  padding: 8px;
  width: 100px;
  text-align: center;
  font-size: 14px;
  margin: 10px 10px;
  margin-left: auto;
  cursor: pointer;
}
</style>

      `,

      zoomable_tree: `
      <template>
   <ul class="tree" :class="tree_direction">
                  <hlx-tree :nodes="treedata" :tree-direction="tree_direction"  zoomable :zoom="this.zoom"/>
      </ul>
</template>
<script>

export default {
  name: "TreeView",
  components: {
    TreeChild,
  },

  watch:{
      tree_direction : function(){
        this.tree_direction
      }
  },
  methods: {

    myFunction(){
      if(this.tree_direction=="vertical"){
        this.tree_direction="horizontal"
      }
      else{
        this.tree_direction="vertical"
      }
    }
  },
  data() {
    return {
    
      tree_direction: "vertical",
      treedata: {
        label: "root",
        child: [
          {
            label: "child1",
            child: [
              {
                label: "child1.2",
                child: [
                  {
                    label: "child1.2.1",
                    child: [
                      {
                        label: "child1.2.11",
                        child: [],
                      },
                    ],
                  },
                  {
                    label: "child1.2.2",
                    child: [],
                  },
                ],
              },
            ],
          },
          {
            label: "child2",
            child: [
              {
                label: "child2.1",
                child: [],
              },
            ],
          },
        ],
      },
    };
  },
};
<\/script>
<style lang="scss" scoped>
.example-body {
  position: relative;
  .tree-container {
    position: relative;
    height: auto;
    width: 100%;
    margin-left: auto;
    padding: 0 599.9px;
    /* border: solid; */
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.custom {
    height: 800px;
    overflow: auto;
  }
  .custom {
    position: sticky;
    left: 0%;
  }
}
.switcher {
  display: flex;
  gap: 10px;
}
.change-btn {
  float: right;
  border: none;
  color: white;
  padding: 8px;
  width: 100px;
  text-align: center;
  font-size: 14px;
  margin: 10px 10px;
  margin-left: auto;
  cursor: pointer;
}
</style>

      `,
      expandable_tree: `
      <template>
   <ul class="tree" :class="tree_direction">
                  <hlx-tree :nodes="treedata" :tree-direction="tree_direction" :collapse-all="true" :expandAll="false" zoomable :zoom="this.zoom"/>
      </ul>
</template>
<script>

export default {
  name: "TreeView",
  components: {
    TreeChild,
  },

  watch:{
      tree_direction : function(){
        this.tree_direction
      }
  },
  methods: {

    myFunction(){
      if(this.tree_direction=="vertical"){
        this.tree_direction="horizontal"
      }
      else{
        this.tree_direction="vertical"
      }
    }
  },
  data() {
    return {
    
      tree_direction: "vertical",
      treedata: {
        label: "root",
        child: [
          {
            label: "child1",
            child: [
              {
                label: "child1.2",
                child: [
                  {
                    label: "child1.2.1",
                    child: [
                      {
                        label: "child1.2.11",
                        child: [],
                      },
                    ],
                  },
                  {
                    label: "child1.2.2",
                    child: [],
                  },
                ],
              },
            ],
          },
          {
            label: "child2",
            child: [
              {
                label: "child2.1",
                child: [],
              },
            ],
          },
        ],
      },
    };
  },
};
<\/script>
<style lang="scss" scoped>
.example-body {
  position: relative;
  .tree-container {
    position: relative;
    height: auto;
    width: 100%;
    margin-left: auto;
    padding: 0 599.9px;
    /* border: solid; */
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.custom {
    height: 800px;
    overflow: auto;
  }
  .custom {
    position: sticky;
    left: 0%;
  }
}
.switcher {
  display: flex;
  gap: 10px;
}
.change-btn {
  float: right;
  border: none;
  color: white;
  padding: 8px;
  width: 100px;
  text-align: center;
  font-size: 14px;
  margin: 10px 10px;
  margin-left: auto;
  cursor: pointer;
}
</style>

      `,

      treedata: {
        label: "All products",
        data: {},

        child: [
          {
            label: "Computers",
            // data: { name: "andree", age: 12 },
            child: [
              {
                label: "Apple",
              },
              {
                label: "AMD",
              },
              {
                label: "Intel",
              },
            ],
          },
          {
            label: "Books",
            // data: { name: "andree", age: 12 },
            child: [
              {
                label: "Fiction",
              },
              {
                label: "Non-fiction",
              },
            ],
          },
          {
            label: "ICs",
          },
        ],
      },
    };
  },
  watch: {
    tree_direction: function () {
      this.tree_direction;
    },
  },
  // mounted() {
  //   console.log("side");
  // },
  async mounted() {
    const spec = await axios.get(
      `https://tmf-upc-ghubgwxrcq-pd.a.run.app/tree/cfs`,
      {
        headers: {
          "hlx-api-key":
            "dbee69aa047d12f22275e281ee432da3:2920ca58abb53b122a1e06c94ceabdc1323d441d980a1f19d1c7f040a5e8910af798fdaf8a7213898efbbb43b74da0e3670964b9d96a4081415cca3beeed7727",
        },
      }
    );

    // const drop = await axios.get(
    //   `https://tmf-upc-ghubgwxrcq-pd.a.run.app/tree/65e990f4c403fbd591917e75`,
    //   {
    //     headers: {
    //       Authorization:
    //         "Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJkZXZlbG9wZXJAaGFsbGV5eC5jb20iLCJjb21wYW55IjoiZGV2ZWxvcGVySGFsbGV5eCIsImlhdCI6MTcxMDU5OTAwNCwiZXhwIjoxNzEwNzcxODA0fQ.imZTY69_8M8CQFXJlvFk7zSdDMtlqwAM0AGxs76yT5xmsAdGcdluNtJuLpwIj3-w1SdeSrh5IfF0M5dXggF11WsCwxadWD_88G8iNuDIYRs2FCBkzVl0rWwCDQRFCHfRBiXVb-pupUEmf5NGHxV1M48X161QbWHbOs8Ve2zBdY-Z25l4Kx3d7afLSBN725pKaTT20aMFOu5fegN3apT0L1-aquHo1HPFYbzX-l8i6Chq4Jd2wsiFVhMtVKCtRjhz8fpp_imGfRr9ZMhraEVFqy0J10dcI4k4nvLXJ3Ye8E5D31d3SE44MWNuQW219q8sZyfK9oWgL71g1AvWxoQQMsfmmfrxy30AAB_3jnzZ_Q32qZiWrsElCO7b_jb6sHnIQl7yUzb3lOLdZH1w6YN6fGmjvbw-xDzDpJy-usc2ukWcMtvxiVt9jyTslKe9ZPzMgidVD4YUrlmyhpVJEbtUJPIF5nRl834yztuq7XGYSXdUIdh90VLMR_hCQL997BdpJurJFj8g3nuOaS4EM_KpNLCcb_9Qri7fG8TwmCxUQikB8MgnV1Wk__OJnLCnWhs-izFCWg2BLc_LaSFZRCSusOkoJ-gyAhzOi5tUXr1WWMsPFG9CgJDAAMc6aDETSX870nnqI9RSRx1sW3O5xvTyxfSuzLSQs0mpIhpUzoU2Smo ",
    //     },
    //   }
    // );
    // this.treedataval = drop.data;
    this.dropdown2data1 = await spec.data;
    console.log(this.dropdown2data1, "dropdown2data1");
  },
  methods: {
    dataReceived(val) {
      console.log(val);
    },
    updatednode(val) {
      console.log(val, "updatednode");
    },
    prevNextPage(nav, name) {
      if (nav === "prev") {
        sessionStorage.setItem("lastname", name);
        this.$router.push({ name: name });
      } else if (nav === "next") {
        sessionStorage.setItem("lastname", name);
        this.$router.push({ name: name });
      }
    },

    showCode(val) {
      document.getElementById(val + "-icon").classList.toggle("active-icon");
      if (document.getElementById(val).style.display === "none") {
        document.getElementById(val).style.display = "block";
      } else if (document.getElementById(val).style.display === "block") {
        document.getElementById(val).style.display = "none";
      }
    },
    myFunction() {
      if (this.tree_direction == "vertical") {
        this.tree_direction = "horizontal";
      } else {
        this.tree_direction = "vertical";
      }
    },
    expand() {
      if (this.isCollapsed) {
        this.collapseAll = this.isCollapsed;
        this.expandAll = !this.isCollapsed;
      } else {
        this.collapseAll = this.isCollapsed;
        this.expandAll = !this.isCollapsed;
      }
      this.isCollapsed = !this.isCollapsed;
    },
  },
};
</script>
<style lang="scss" scoped>
.example-body {
  position: relative;
  .tree-container {
    position: relative;
    height: auto;
    width: 100%;
    margin-left: auto;
    padding: 0 599.9px;
    /* border: solid; */
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.custom {
    height: 800px;
    overflow: auto;
  }
  .custom {
    position: sticky;
    left: 0%;
  }
}
.switcher {
  display: flex;
  gap: 10px;
}
.change-btn {
  float: right;
  border: none;
  color: white;
  padding: 8px;
  width: 100px;
  text-align: center;
  font-size: 14px;
  margin: 10px 10px;
  margin-left: auto;
  cursor: pointer;
}
</style>
